import JwtService from "@/core/services/JwtService";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import cloneDeep from "lodash/cloneDeep";
import { App } from "vue";
import VueAxios from "vue-axios";

import router from "@/router";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import store from "@/store/index";
import { AxiosResponse } from "axios";
import { AUTH_TOKEN_ERROR_MESSAGES } from "../constants/const";
import { getPerPageFromUserSettings } from "../helpers/userSystemSettings";
import {
    getIdDonViFromLocalStorage,
    getYearFromLocalStorage,
} from "../helpers/utils";
import { applyDashboardVersionsFromResponse } from "@/core/helpers/apply-dashboard-versions";

const getErrorStatus = (error: AxiosError): number | undefined =>
  error.response?.data?.status ?? error.response?.status;

const getErrorMessage = (error: AxiosError): string =>
  (error.response?.data as { data?: { msg?: string } })?.data?.msg ?? "";

/** Backend signals an invalid/expired token via these messages (any HTTP status). */
const isTokenInvalidError = (error: AxiosError): boolean => {
  const message = getErrorMessage(error).trim().toLowerCase();

  return AUTH_TOKEN_ERROR_MESSAGES.some(
    (tokenError) => tokenError.trim().toLowerCase() === message
  );
};
const HOME_ROUTE_NAME = "dashboard";
const LOGIN_ROUTE_NAME = "sign-in";

/**
 * Auth endpoints that manage their own error UX and must not trigger
 * automatic redirects (e.g. a failed login should stay on the login page).
 */
const SKIP_REDIRECT_ENDPOINTS = [
  "/api/v1/auth/login",
  "/api/v1/auth/register",
  "/api/v1/auth/refresh",
  // DonVi ketoan sync manages its own 403/404 UX inside the wizard.
  "/api/v1/don-vi/ketoan/",
  "/api/v1/don-vi/geojson/",
  // Cai-dat config page manages its own 403/404 UX.
  "/api/v1/cai-dat/",
];

const isRedirectableRequest = (error: AxiosError): boolean => {
  const url = error.config?.url ?? "";
  return !SKIP_REDIRECT_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

const redirectToHome = () => {
  if (router.currentRoute.value.name !== HOME_ROUTE_NAME) {
    router.push({ name: HOME_ROUTE_NAME });
  }
};

const redirectToLogin = () => {
  if (router.currentRoute.value.name !== LOGIN_ROUTE_NAME) {
    router.push({ name: LOGIN_ROUTE_NAME });
  }
};

/** 401 / token expired: clear session then send the user to the login page. */
const handleUnauthenticated = async () => {
  store.commit(Mutations.PURGE_AUTH);
  await store.dispatch(Actions.RESET_ABILITIES);

  redirectToLogin();
};

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;

    ApiService.vueInstance.use(VueAxios, axios);

    // Development: relative `/api/...` → Vue Dev Server proxy (same-origin, no CORS).
    // Production: absolute backend host from env (frontend may be on a different domain).
    ApiService.vueInstance.axios.defaults.baseURL =
      process.env.NODE_ENV === "development"
        ? ""
        : process.env.VUE_APP_API_URL;

    ApiService.vueInstance.axios.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        const configClone = cloneDeep(config);

        const url = String(configClone.url ?? "");
        const isAbilitiesEndpoint = url.includes("/api/v1/me/abilities");
        const isCacheVersionsPoll = url.includes("/dashboard/cache-versions");
        const skipAbilitiesSync =
          isAbilitiesEndpoint ||
          isCacheVersionsPoll ||
          configClone.meta?.skipAbilitiesSync === true;
        const isPublicAuthEndpoint = SKIP_REDIRECT_ENDPOINTS.some((endpoint) =>
          url.includes(endpoint)
        );
        const isAuthenticated = !!JwtService.getToken();

        // Ensure abilities once if missing. Never attach to cache-versions polling.
        if (!skipAbilitiesSync && isAuthenticated && !isPublicAuthEndpoint) {
          await store.dispatch(Actions.LOAD_ABILITIES);
        }

        // Check permission requirements in config.meta
        if (!isAbilitiesEndpoint && configClone.meta) {
          const needPerm = configClone.meta.perm;
          const needPermAll = configClone.meta.permAll;
          const needPermAny = configClone.meta.permAny;

          if (needPerm || needPermAll || needPermAny) {
            const authzModule = (
              store.state as { AuthzModule?: { permissions: Set<string> } }
            ).AuthzModule;

            if (!authzModule || !authzModule.permissions) {
              // AuthzModule not available, deny permission
              const cancelError = new axios.Cancel("PERMISSION_DENIED");
              return Promise.reject(cancelError);
            }

            const permissions = authzModule.permissions;
            let hasPermission = false;

            // Check perm (single permission or array - all required)
            if (needPerm) {
              if (Array.isArray(needPerm)) {
                hasPermission = needPerm.every((perm) => permissions.has(perm));
              } else {
                hasPermission = permissions.has(needPerm);
              }
            }
            // Check permAll (all permissions required)
            else if (needPermAll) {
              hasPermission =
                Array.isArray(needPermAll) && needPermAll.length > 0
                  ? needPermAll.every((perm) => permissions.has(perm))
                  : false;
            }
            // Check permAny (any permission required)
            else if (needPermAny) {
              hasPermission =
                Array.isArray(needPermAny) && needPermAny.length > 0
                  ? needPermAny.some((perm) => permissions.has(perm))
                  : false;
            }

            if (!hasPermission) {
              // Cancel request before network call
              const cancelError = new axios.Cancel("PERMISSION_DENIED");
              // Return a rejected promise to cancel the request
              return Promise.reject(cancelError);
            }
          }
        }

        // Set request headers
        const idDonVi = getIdDonViFromLocalStorage();
        const requestPerPage = configClone.headers?.perpage;
        configClone.headers = {
          ...configClone.headers,
          Authorization: `Bearer ${JwtService.getToken()}`,
          Accept: "application/json",
          year: getYearFromLocalStorage(),
          perpage: (requestPerPage ?? getPerPageFromUserSettings()).toString(),
          ...(!!JwtService.getToken() &&
            idDonVi && {
              idDonVi: idDonVi.toString(),
            }),
        };

        return configClone;
      }
    );

    ApiService.vueInstance.axios.interceptors.response.use(
      (response: AxiosResponse) => {
        const url = String(response.config?.url ?? "");
        // Poller already applies versions; skip to avoid duplicate invalidation work.
        if (!url.includes("/dashboard/cache-versions")) {
          try {
            applyDashboardVersionsFromResponse(response.data);
          } catch {
            // Never block API consumers on dashboard version sync.
          }
        }
        return response;
      },
      async (error: AxiosError) => {
        // Silently ignore PERMISSION_DENIED errors - no toast or console spam
        if (
          error.code === "PERMISSION_DENIED" ||
          error.message === "PERMISSION_DENIED" ||
          String(error.message ?? "").includes("PERMISSION_DENIED")
        ) {
          // Return rejected promise but don't trigger other error handlers
          return Promise.reject(error);
        }

        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        const errorStatus = getErrorStatus(error);

        if (errorStatus === 401 && !originalRequest?._retry) {
          originalRequest._retry = true; // Mark this request as retried

          try {
            // Call your refresh token API endpoint
            const response = await ApiService.vueInstance.axios.post(
              "/api/v1/auth/refresh",
              {
                refresh_token: JwtService.getToken(),
              }
            );

            // If refresh successful, update the token
            if (response.data.data) {
              store.commit(Mutations.SET_AUTH, response.data.data);

              store.commit(Mutations.SET_USER, response.data.data.user);

              // Update Authorization header with new token
              originalRequest.headers[
                "Authorization"
              ] = `Bearer ${response.data.data.access_token}`;

              // Retry the original request with new token
              return ApiService.vueInstance.axios(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed → session is no longer valid.
            if (isRedirectableRequest(error)) {
              await handleUnauthenticated();
            }

            return Promise.reject(refreshError);
          }
        }

        if (isRedirectableRequest(error)) {
          if (errorStatus === 401 || isTokenInvalidError(error)) {
            // 401 or token invalid/expired (even on a 403) →
            // logout + go to login page.
            await handleUnauthenticated();
          } else if (errorStatus === 403 || errorStatus === 404) {
            // Forbidden / not found → send back to homepage.
            redirectToHome();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params);
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @returns Promise<AxiosResponse>
   */
  public static get(
    resource: string,
    slug = "" as string
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(`${resource}/${slug}`);
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static post(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`, params);
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}`, params);
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource);
  }
}

export default ApiService;
